import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { getOrganizationClientProfileNotificationCountCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';
import { useCallback, useMemo } from 'react';

const intl = getIntl('useOrganizationClientProfileUnreadInAppNotificationCount', {
    loadError: 'Error loading profile notification counts',
});

export type UseOrganizationClientProfileUnreadInAppNotificationCountArgs = {
    clientMedicalProfileId?: string;
};

export type UseOrganizationClientProfileUnreadInAppNotificationCountResponse = {
    unreadInAppNotificationCount: number;
    isLoading: boolean;
    getUnreadInAppNotificationCountForClientMedicalProfile: (clientMedicalProfileId: string) => number;
};

export const useOrganizationClientProfileUnreadInAppNotificationCount = ({
    clientMedicalProfileId,
}: UseOrganizationClientProfileUnreadInAppNotificationCountArgs): UseOrganizationClientProfileUnreadInAppNotificationCountResponse => {
    const organizationId = useOrganizationId();
    const profilesUnreadInAppNotificationCountQuery = useQuery({
        queryKey: getOrganizationClientProfileNotificationCountCacheKey(),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.organizationService.GetClientProfileNotificationCountsResponse>(
                urls.api.organizations.clientRecords.clientProfileNotificationCounts(organizationId),
            );
            return response;
        },
    });
    useQueryErrorNotify(profilesUnreadInAppNotificationCountQuery, intl('loadError'));

    const getUnreadInAppNotificationCountForClientMedicalProfile = useCallback(
        (clientMedicalProfileId: string) => {
            return (
                profilesUnreadInAppNotificationCountQuery.data?.counts.find(
                    (profile) => profile.organizationClientMedicalProfileId === clientMedicalProfileId,
                )?.notificationCount || 0
            );
        },
        [profilesUnreadInAppNotificationCountQuery.data],
    );

    const count = useMemo(() => {
        return clientMedicalProfileId
            ? getUnreadInAppNotificationCountForClientMedicalProfile(clientMedicalProfileId)
            : 0;
    }, [clientMedicalProfileId, getUnreadInAppNotificationCountForClientMedicalProfile]);

    return {
        unreadInAppNotificationCount: count,
        getUnreadInAppNotificationCountForClientMedicalProfile,
        isLoading: profilesUnreadInAppNotificationCountQuery.isLoading,
    };
};
